
import { Component, Provide, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import StyleInjector from '@/components/StyleInjector.vue';
import AppFooter from '@/components/footer/AppFooter.vue';
import AppHeader from './components/nav/AppHeader.vue';

const Experiments = () => import(/* webpackChunkName: "experiments" */ '@/components/experiments/Experiments.vue');
const LowPriorityComponents = () =>
	import(/* webpackChunkName: "low-priority-components" */ '@/components/LowPriorityComponents.vue');
const LoginFlow = () => import(/* webpackChunkName: "login-flow" */ '@/components/login/LoginFlow.vue');

import { CollectionType } from '@/enums/collection-type';

import Provider from '@/interfaces/provider';

import type { FilterCollection } from '@/stores/filter.store';

// @ts-ignore
import LazyHydrate from 'vue-lazy-hydration';
// @ts-ignore
import NoSSR from 'vue-no-ssr';

// make NoSSR a globaly available component
Vue.component('NoSSR', NoSSR);
Vue.component('LazyHydrate', LazyHydrate);

// AppFooter global to avoid CLS
Vue.component('AppFooter', AppFooter);

// SSR Content
import ContentSSR from '@/components/ContentSSR.vue';
// eslint-disable-next-line
Vue.component('Content', ContentSSR);

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

const Constant = namespace('constant');
const Filter = namespace('filter');
const Language = namespace('language');
const Meta = namespace('meta');
const Routing = namespace('routing');
const User = namespace('user');
@Component({
	name: 'SSRApp',
	components: {
		AppHeader,
		Experiments,
		LowPriorityComponents,
		StyleInjector,
		LoginFlow,
	},
	metaInfo() {
		return this.head;
	},
})
export default class App extends Vue {
	@Constant.Getter providersByShortName: Record<string, Provider>;

	@Filter.Getter filters: (collectionType: CollectionType) => FilterCollection;

	@Language.Getter locale: string;

	@Meta.Getter head: any;

	@Routing.Getter lastActiveCollectionType: null | CollectionType;

	@User.Getter isLoggedIn: () => boolean;

	@Provide('disableScroll')
	disableScroll() {
		if (process.client) {
			document.body.style.overflowY = 'hidden';
		}
	}

	@Provide('enableScroll')
	enableScroll() {
		if (process.client) {
			document.body.style.overflowY = 'auto';
		}
	}

	@Provide('lowPriorityCallback')
	lowPriorityCallback(cb: Function) {
		if ('requestIdleCallback' in window) {
			window.requestIdleCallback(() => cb());
		} else {
			this.$nextTick(() => cb());
		}
	}

	async mounted() {
		const { initStoreWatchers } = await import(/* webpackChunkName: "main.common" */ '@/main.common');
		initStoreWatchers(this.$context);

		this.initPerformanceMonitor();

		const { initCoreWebVitalsMonitor } = await import('@/helpers/tracking/cwv-helper');
		initCoreWebVitalsMonitor();

		Vue.nextTick(() => {
			const int = setInterval(() => {
				// remove loader
				const loader = document.querySelector('#loader');
				loader?.parentNode?.removeChild(loader);
				clearInterval(int);
			}, 50);

			// reactivate scroll on page rendered
			this.enableScroll();
		});
	}

	async initPerformanceMonitor() {
		const { TrackingHelper } = await import('@/helpers/tracking/tracking-helper');
		const { SnowplowPerformanceContext } = await import('@/helpers/tracking/providers');
		const { PerformanceMonitor } = await import('@/helpers/tracking/performance/PerformanceMonitor');

		let performanceMonitor: any;

		try {
			performanceMonitor = new PerformanceMonitor();
		} catch (error) {
			console.error(error);
		}

		performanceMonitor.onInitialMetricsCollected().then(async () => {
			await TrackingHelper.trackEvent('performance_metrics', { action: 'initial', nonInteraction: true }, [
				new SnowplowPerformanceContext(
					performanceMonitor.performanceMetrics,
					performanceMonitor.getNavigationEntry()
				),
			]);
		});

		performanceMonitor.onFinalMetricsCollected().then(async () => {
			await TrackingHelper.trackEvent('performance_metrics', { action: 'loaded', nonInteraction: true }, [
				new SnowplowPerformanceContext(
					performanceMonitor.performanceMetrics,
					performanceMonitor.getNavigationEntry()
				),
			]);
		});
	}

	get hideAppHeader() {
		return this.$route.name === 'app.streaming-charts';
	}
}
